import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Button,
    Chip,
    Dialog,
    DialogContent,
    IconButton,
    LinearProgress,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {
    findBestRendition,
    formatBytes,
    getVideoId,
    IBestRendition,
    IStoryVideoOption,
    IStoryViewerStory,
    renditionUrl,
    StoryVideoVariant,
    VIDEO_RESOLUTIONS,
} from '../../lib/storyViewer';
import { useVideoDownload } from './useVideoDownload';
import { formatStoryDate, storyFileBase, storyPoster } from './StoryCard';

interface IProps {
    story: IStoryViewerStory | null;
    open: boolean;
    onClose: () => void;
    /** Position of the open story inside the current list, 0-based. */
    index?: number;
    total?: number;
    onPrev?: () => void;
    onNext?: () => void;
}

export const getStoryVideoOptions = (story: IStoryViewerStory): IStoryVideoOption[] => {
    const options: IStoryVideoOption[] = [];
    if (getVideoId(story.videoLink)) options.push({ variant: 'main', label: 'Main video', videoLink: story.videoLink as string, poster: storyPoster(story) });
    if (getVideoId(story.videoLinkSecondary)) options.push({ variant: 'secondary', label: 'Secondary video', videoLink: story.videoLinkSecondary as string, poster: story.imageLinkSecondary });
    if (getVideoId(story.videoLinkProduction)) options.push({ variant: 'production', label: 'Production video', videoLink: story.videoLinkProduction as string, poster: story.imageLinkProduction });
    return options;
};

const VARIANT_SUFFIX: Record<StoryVideoVariant, string> = { main: '', secondary: '-secondary', production: '-production' };

const DetailRow = ({ label, value }: { label: string; value?: React.ReactNode }) => (
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'baseline' }}>
        <Typography variant="caption" sx={{ color: 'text.secondary', minWidth: 72 }}>{label}</Typography>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>{value || '—'}</Typography>
    </Box>
);

const NAV_BUTTON_SX = {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    zIndex: 2,
    bgcolor: 'rgba(0,0,0,0.45)',
    color: '#fff',
    '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
    '&.Mui-disabled': { color: 'rgba(255,255,255,0.3)', bgcolor: 'rgba(0,0,0,0.25)' },
} as const;

const StoryViewerModal = ({ story, open, onClose, index, total, onPrev, onNext }: IProps) => {
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
    const download = useVideoDownload();
    const [variant, setVariant] = useState<StoryVideoVariant>('main');
    const [best, setBest] = useState<IBestRendition | null | undefined>(undefined);

    const videoOptions = useMemo(() => (story ? getStoryVideoOptions(story) : []), [story]);
    const current = videoOptions.find((option) => option.variant === variant) || videoOptions[0] || null;
    const currentVideoId = current ? getVideoId(current.videoLink) : null;

    const hasPrev = Boolean(onPrev) && index !== undefined && index > 0;
    const hasNext = Boolean(onNext) && index !== undefined && total !== undefined && index < total - 1;

    useEffect(() => { setVariant('main'); }, [story?.storyId]);

    useEffect(() => {
        if (!open) { download.cancel(); }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    useEffect(() => {
        if (!currentVideoId || !open) { setBest(undefined); return; }
        let cancelled = false;
        setBest(undefined);
        findBestRendition(currentVideoId).then((result) => { if (!cancelled) setBest(result); });
        return () => { cancelled = true; };
    }, [currentVideoId, open]);

    const tagsByCategory = useMemo(() => {
        const groups = new Map<string, string[]>();
        (story?.tags || []).forEach((tag) => {
            const key = tag.categoryName || tag.category || 'Other';
            groups.set(key, [...(groups.get(key) || []), tag.tagName]);
        });
        return Array.from(groups.entries());
    }, [story]);

    const { state } = download;
    // Progress belongs to the video it was started for; after switching stories the button is available again.
    const busyHere = (state.status === 'probing' || state.status === 'downloading') && state.videoLink === current?.videoLink;
    const percent = state.progress && state.progress.total > 0
        ? Math.min(100, Math.round((state.progress.loaded / state.progress.total) * 100))
        : null;

    const handleDownload = () => {
        if (!story || !current || busyHere) return;
        download.start(current.videoLink, storyFileBase(story, VARIANT_SUFFIX[current.variant]));
    };

    const handleKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === 'ArrowLeft' && hasPrev) { event.preventDefault(); onPrev?.(); }
        if (event.key === 'ArrowRight' && hasNext) { event.preventDefault(); onNext?.(); }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            fullScreen={fullScreen}
            onKeyDown={handleKeyDown}
            PaperProps={{ sx: { borderRadius: fullScreen ? 0 : 2 } }}
        >
            <IconButton aria-label="Close" onClick={onClose} sx={{ position: 'absolute', top: 8, right: 8, zIndex: 2, bgcolor: 'background.paper', boxShadow: 1, '&:hover': { bgcolor: 'action.hover' } }} size="small">
                <CloseIcon fontSize="small" />
            </IconButton>
            <DialogContent sx={{ p: 0 }}>
                {story && (
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, minHeight: { md: 560 } }}>
                        <Box sx={{ position: 'relative', flex: '1 1 60%', bgcolor: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: { xs: 320, md: 560 } }}>
                            {(onPrev || onNext) && (
                                <>
                                    <Tooltip title="Previous story (←)" arrow placement="right">
                                        <span style={{ position: 'absolute', left: 8, top: '50%', zIndex: 2 }}>
                                            <IconButton aria-label="Previous story" onClick={onPrev} disabled={!hasPrev} sx={{ ...NAV_BUTTON_SX, position: 'relative', top: 0, left: 0 }}>
                                                <ChevronLeftIcon />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                    <Tooltip title="Next story (→)" arrow placement="left">
                                        <span style={{ position: 'absolute', right: 8, top: '50%', zIndex: 2 }}>
                                            <IconButton aria-label="Next story" onClick={onNext} disabled={!hasNext} sx={{ ...NAV_BUTTON_SX, position: 'relative', top: 0, right: 0 }}>
                                                <ChevronRightIcon />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                </>
                            )}
                            {current && currentVideoId ? (
                                <video
                                    key={currentVideoId}
                                    controls
                                    autoPlay
                                    playsInline
                                    poster={current.poster || undefined}
                                    style={{ width: '100%', height: '100%', maxHeight: fullScreen ? '60vh' : '80vh', objectFit: 'contain', display: 'block' }}
                                >
                                    {VIDEO_RESOLUTIONS.map((resolution) => (
                                        <source key={resolution} src={`${renditionUrl(currentVideoId, resolution)}#t=0.001`} type="video/mp4" />
                                    ))}
                                </video>
                            ) : (
                                <Box sx={{ color: '#fff', p: 4, textAlign: 'center' }}>
                                    {storyPoster(story) ? (
                                        <img src={storyPoster(story) as string} alt="" style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }} />
                                    ) : 'This story has no video'}
                                </Box>
                            )}
                        </Box>
                        <Box sx={{ flex: '1 1 40%', p: 3, pt: 5, display: 'flex', flexDirection: 'column', gap: 2, minWidth: { md: 320 } }}>
                            <Box>
                                <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                                    Story #{story.storyId}
                                    {index !== undefined && total !== undefined && (
                                        <Typography component="span" variant="body2" sx={{ color: 'text.secondary', ml: 1 }}>{index + 1} / {total}</Typography>
                                    )}
                                </Typography>
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>{formatStoryDate(story.date)}</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                <DetailRow label="Occasion" value={story.occasion} />
                                <DetailRow label="Recipient" value={story.recipient} />
                                <DetailRow label="Product" value={story.product} />
                            </Box>
                            {videoOptions.length > 1 && (
                                <ToggleButtonGroup
                                    size="small"
                                    exclusive
                                    value={current?.variant || 'main'}
                                    onChange={(_event, value: StoryVideoVariant | null) => { if (value) setVariant(value); }}
                                >
                                    {videoOptions.map((option) => (
                                        <ToggleButton key={option.variant} value={option.variant} sx={{ textTransform: 'none' }}>{option.label}</ToggleButton>
                                    ))}
                                </ToggleButtonGroup>
                            )}
                            {current && (
                                <Box sx={{ p: 1.5, borderRadius: 1.5, bgcolor: 'action.hover', display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                        {best === undefined
                                            ? 'Checking available quality…'
                                            : best
                                                ? `Best available: ${best.resolution}p${best.size ? ` · ${formatBytes(best.size)}` : ''}`
                                                : 'No downloadable file found for this video'}
                                    </Typography>
                                    {busyHere ? (
                                        <Box>
                                            <LinearProgress variant={percent === null ? 'indeterminate' : 'determinate'} value={percent ?? 0} sx={{ borderRadius: 1, mb: 0.5 }} />
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                                    {state.status === 'probing'
                                                        ? 'Preparing…'
                                                        : `${percent ?? 0}% · ${formatBytes(state.progress?.loaded || 0)}${state.progress?.total ? ` of ${formatBytes(state.progress.total)}` : ''}`}
                                                </Typography>
                                                <Button size="small" onClick={download.cancel}>Cancel</Button>
                                            </Box>
                                        </Box>
                                    ) : (
                                        <Button
                                            variant="contained"
                                            startIcon={<DownloadIcon />}
                                            onClick={handleDownload}
                                            disabled={best === null}
                                        >
                                            Download
                                        </Button>
                                    )}
                                </Box>
                            )}
                            {tagsByCategory.length > 0 && (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Tags</Typography>
                                    {tagsByCategory.map(([category, names]) => (
                                        <Box key={category} sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
                                            <Typography variant="caption" sx={{ color: 'text.secondary', minWidth: 110, textTransform: 'uppercase', letterSpacing: 0.4 }}>{category}</Typography>
                                            {names.map((name) => <Chip key={`${category}-${name}`} size="small" label={name} variant="outlined" sx={{ height: 22, fontSize: 11 }} />)}
                                        </Box>
                                    ))}
                                </Box>
                            )}
                        </Box>
                    </Box>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default StoryViewerModal;
